/**
 * Normalizes a single value: numeric strings with commas (e.g. "123,4" or "1,234.56")
 * are converted to numbers; other values are returned unchanged.
 * @param {*} value - Any value
 * @returns {*} Normalized value (number if numeric string, else same type)
 */
export const normalizeValue = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') return value;
        const withoutCommas = trimmed.replace(/,/g, '');
        const num = parseFloat(withoutCommas);
        return Number.isNaN(num) ? value : num;
    }
    return value;
};

/**
 * Recursively normalizes an object/array: numeric strings (e.g. "123,4" → 1234)
 * are converted to numbers at any nesting level; other primitives and nested structures are preserved.
 * @param {*} data - Object, array, or primitive
 * @returns {*} New structure with normalized numeric values
 */
export const getNormalizedValues = (data) => {
    if (data === null || data === undefined) return data;
    if (typeof data === 'number' && !Number.isNaN(data)) return data;
    if (typeof data === 'string') return normalizeValue(data);
    if (Array.isArray(data)) return data.map(getNormalizedValues);
    if (typeof data === 'object' && !(data instanceof File) && !(data instanceof Blob)) {
        const out = {};
        for (const key of Object.keys(data)) {
            out[key] = getNormalizedValues(data[key]);
        }
        return out;
    }
    return data;
};

export const markAllControlsDirtyAndTouched = (control) => {
    if (typeof control.markAsDirty === 'function') {
        control.markAsDirty({ emitEvent: true });
        control.markAsTouched({ emitEvent: true });
    }

    if (control.controls && typeof control.controls === 'object') {
        Object.values(control.controls).forEach(child => markAllControlsDirtyAndTouched(child));
    }

    return control;
}

export const printInvalidControls = (form) => {
    const invalidControls = Object.entries(form.controls || {})
        .filter(([, ctrl]) => ctrl && ctrl.invalid)
        .map(([name, ctrl]) => ({ name, errors: ctrl.errors, value: ctrl.value }));
    // eslint-disable-next-line no-console
    console.log('Invalid form controls:', invalidControls);
}

export const applicationPrefixValidator = (control) => {
    const formGroup = control.parent;
    // Allow alphanumeric digits in a range of 2 to 5 characters
    const val = control.value;
    if (
        val === "" ||   
        /^[a-zA-Z0-9]{2,5}$/.test(val)
    ) {
        return null;
    }
    return { invalidApplicationPrefix: true };
}